import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined"
import React, { useCallback, useEffect, useState } from "react"
import TextField from "@mui/material/TextField"
import InputAdornment from "@mui/material/InputAdornment"
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined"
import { Pagination } from "../Pagination"
import { getLeaderboard } from "../../../../api/tournaments"
import { getMedalColor } from "../../utils/getMedalColor"
import { useAppSelector } from "../../../../store"

export default function Leaderboard({ tournament, cache, onCacheChange }) {
  const { user } = useAppSelector((state) => state.user)
  const [perPage, setPerPage] = useState(20)
  const [currentPage, setCurrentPage] = useState(1)
  const [leaderboard, setLeaderboard] = useState(cache?.data || [])
  const [isLoading, setIsLoading] = useState(!cache?.data?.length)
  const [search, setSearch] = useState("")

  const startIndex = (currentPage - 1) * perPage
  const visibleRows = leaderboard.slice(startIndex, startIndex + perPage)

  const loadLeaderboard = useCallback(
    async ({ silent = false, searchValue = "" } = {}) => {
      if (!silent) {
        setIsLoading(true)
      }

      const normalizedSearch = searchValue.trim()

      const { success, leaderboard: rows } = await getLeaderboard(
        tournament.id,
        { userWallet: user?.address, search: normalizedSearch },
      )

      if (success) {
        setLeaderboard(rows)

        if (!normalizedSearch) {
          onCacheChange({
            data: rows,
            updatedAt: Date.now(),
            userAddress: user?.address || null,
            search: "",
          })
        }
      }

      if (!silent) {
        setIsLoading(false)
      }
    },
    [onCacheChange, tournament.id, user?.address],
  )

  useEffect(() => {
    const cacheAge = Date.now() - (cache?.updatedAt || 0)
    const isSameUser = (cache?.userAddress || null) === (user?.address || null)
    const hasFreshCache = cacheAge < 30000 && isSameUser

    if (hasFreshCache) {
      setLeaderboard(cache.data)
      setIsLoading(false)
      return
    }

    loadLeaderboard()
  }, [
    cache?.data,
    cache?.updatedAt,
    cache?.userAddress,
    loadLeaderboard,
    user?.address,
  ])

  useEffect(() => {
    const normalizedSearch = search.trim()

    if (!normalizedSearch) {
      setCurrentPage(1)

      if (cache?.data?.length) {
        setLeaderboard(cache.data)
        setIsLoading(false)
      }

      return
    }

    if (normalizedSearch.length < 3) {
      return
    }

    const timeoutId = setTimeout(() => {
      setCurrentPage(1)
      loadLeaderboard({ searchValue: normalizedSearch })
    }, 400)

    return () => {
      clearTimeout(timeoutId)
    }
  }, [cache?.data, loadLeaderboard, search])

  return (
    <Box
      sx={{
        border: "1px solid rgba(196,150,84,0.2)",
        borderRadius: 1,
        background: "linear-gradient(180deg, #000 60%, #121212 100%)",
        overflowX: "auto",
      }}
    >
      <Box
        sx={{
          minWidth: {
            xs: 650,
            sm: "auto",
          },
          p: {
            xs: 2,
            sm: 2.5,
            md: 3,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: {
              xs: "flex-start",
              sm: "center",
            },
            justifyContent: "space-between",
            gap: 2,
            mb: 1.8,
          }}
        >
        <Typography
          sx={{
            color: "#f1f5f9",
            fontSize: {
              xs: 22,
              md: 27,
            },
            fontWeight: 900,
            lineHeight: 1,
            letterSpacing: "-0.03em",
          }}
        >
          Live Leaderboard
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#b7c0ca",
            fontSize: {
              xs: 12,
              sm: 14,
            },
            whiteSpace: "nowrap",
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#45d267",
              boxShadow: "0 0 10px rgba(69, 210, 103, 0.8)",
            }}
          />
          Updates every 30 seconds
        </Box>
      </Box>

      {/* Search input and button Find me */}
      <Box
        sx={{
          display: "flex",
          alignItems: {
            xs: "flex-start",
            sm: "center",
          },
          justifyContent: "space-between",
          gap: 2,
          mb: 1.8,
        }}
      >
        <TextField
          placeholder="Search by username or address..."
          size="small"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
          }}
          sx={{
            width: { xs: "100%", sm: 420 },
            ".MuiInputBase-root": {
              color: "#fff",
              borderRadius: 1,
              backgroundColor: "rgba(0,0,0,0.35)",
            },
            ".MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(255,255,255,0.16)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "rgba(196,150,84,0.45)",
            },
            ".MuiInputBase-input": {
              py: 1.15,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchOutlinedIcon sx={{ color: "#b7bbc3" }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "44px 44px minmax(150px, 1fr) minmax(150px, 1fr) 86px 92px",
            sm: "44px 62px minmax(190px, 1fr) minmax(190px, 1fr) 120px 120px",
          },
          alignItems: "center",
          px: {
            xs: 1,
            sm: 1.4,
          },
          mx: 1.4,
          py: 1,
          color: "#9aa5b1",
          fontSize: 14,
          fontWeight: 800,
        }}
      >
        <Box textAlign="center"></Box>
        <Box textAlign="center">#</Box>
        <Box>PLAYER</Box>
        <Box>USERNAME</Box>
        <Box textAlign="center">SCORE</Box>
        <Box textAlign="center">REWARD</Box>
      </Box>

      <Box>
        {visibleRows.map((row) => {
          const isCurrentUser = row.is_current_user
          return (
            <Box
              key={`${row.place}-${row.player}`}
              sx={{
                position: "relative",
                display: "grid",
                gridTemplateColumns: {
                  xs: "44px 44px minmax(150px, 1fr) minmax(150px, 1fr) 86px 92px",
                  sm: "44px 62px minmax(190px, 1fr) minmax(190px, 1fr) 120px 120px",
                },
                alignItems: "center",
                minHeight: {
                  xs: 38,
                  sm: 44,
                },
                px: {
                  xs: isCurrentUser ? 1.8 : 1,
                  sm: isCurrentUser ? 2.8 : 1.4,
                },
                mx: isCurrentUser ? 0 : 1.4,
                fontWeight: 800,
                color: isCurrentUser ? "#bc8c4b" : "#dedede",
                border: isCurrentUser
                  ? "1px solid #bc8c4b"
                  : "1px solid rgba(120, 145, 170, 0.1)",
                borderRadius: isCurrentUser ? 1 : 0,
                background: isCurrentUser
                  ? "rgba(215,155,39,0.1)"
                  : "transparent",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  color: getMedalColor(row.medal),
                }}
              >
                {row.medal ? (
                  <EmojiEventsOutlinedIcon
                    sx={{
                      fontSize: 22,
                    }}
                  />
                ) : (
                  isCurrentUser && (
                    <Box
                      sx={{
                        px: 1,
                        py: 0.45,
                        color: "#ffc13e",
                        border: "1px solid rgba(255,193,62,0.65)",
                        borderRadius: "4px",
                        fontSize: 12,
                        fontWeight: 900,
                      }}
                    >
                      YOU
                    </Box>
                  )
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "inherit",
                    fontWeight: 900,
                  }}
                >
                  {row.place}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: "inherit",
                  fontWeight: 850,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.player}
              </Typography>

              <Typography
                sx={{
                  fontSize: "inherit",
                  fontWeight: 850,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.username}
              </Typography>

              <Typography
                sx={{
                  fontSize: "inherit",
                  fontWeight: 850,
                  textAlign: "center",
                }}
              >
                {row.score}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.6,
                }}
              >
                {row.reward !== "—" && (
                  <EmojiEventsOutlinedIcon
                    sx={{
                      color: "#d8c55e",
                      fontSize: {
                        xs: 14,
                        sm: 16,
                      },
                    }}
                  />
                )}

                <Typography
                  sx={{
                    fontSize: "inherit",
                    fontWeight: 900,
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.reward}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Box>

      {/* Table footer */}
      <Pagination
        currentPage={currentPage}
        totalItems={leaderboard.length}
        perPage={perPage}
        onPageChange={setCurrentPage}
        onPerPageChange={setPerPage}
      />
      </Box>
    </Box>
  )
}
